/**
 * Custom hook untuk mengambil data occupancy harian.
 * 
 * API Endpoint: GET `/api/v4/occupancy-daily`
 * API Version: 4.0
 * 
 * @param {string} property_id - ID property (required)
 * @param {string} group_by - Group by field (default: 'date')
 * @returns {object} - Return value berupa object dengan key `data`, `isFetching`, `isError`, dll.
 * @example
 * const { data, isFetching, isError } = useOccupancyDailyV4({ property_id: '123', group_by: 'date' });
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { OccupancyDailyResponseV4Schema } from "../response/OccupancyDailyResponseV4";

const API_VERSION = 'v4';

interface OccupancyDailyV4Props {
    property_id: string;
    start: string;
    end: string;
    group_by?: string;
}

const useOccupancyDailyV4 = ({ property_id, start, end, group_by = 'date' }: OccupancyDailyV4Props) => {
    return useQuery({
        queryKey: ['occupancy-daily', 'v4', property_id, start, end, group_by],
        queryFn: async () => {
            try {
                const response = await privateApi.get(`/${API_VERSION}/occupancy-daily`, {
                    params: {
                        filter: {
                            property_id,
                            start,
                            end,
                        },
                        group_by,
                    }
                });

                // Validasi response
                const validationResult = OccupancyDailyResponseV4Schema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Occupancy Daily V4 data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Occupancy Daily V4`, error);
                throw error;
            }
        },
        enabled: !!property_id && !!start && !!end,
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false
    });
};

export default useOccupancyDailyV4;
